// Orchestrates a head-to-head benchmark run: iterates the video file
// once, feeds each frame to every selected detector, records per-detector
// timings and hits. Detectors run sequentially within a frame so their
// decode-time measurements don't overlap on the main thread.
//
// Video is only decoded once regardless of how many detectors are
// selected — this is the whole point of doing it here vs. on the live
// scan surface: measure each detector on an identical input stream.

import { decodeFiducials, ensureOpenCv } from '../continuous-scan/lib/fiducialDecoder';
import { ensureTesseract } from '../continuous-scan/lib/ensureTesseract';
import { decodeText } from '../continuous-scan/lib/decodeText';
import { decodeFrame, type DecodeOutcome } from '../continuous-scan/lib/qrDecoder';
import type { ParsedTextPattern } from '../continuous-scan/lib/textPattern';
import type { ActiveFormat } from '../continuous-scan/types';
import { iterateVideoFrames } from './videoFrameIterator';

export interface BenchmarkHit {
  value: string;
  firstSeenFrame: number;
  firstSeenSeconds: number;
}

export interface BenchmarkResult {
  detector: ActiveFormat;
  framesProcessed: number;
  detectorTimeMs: number;
  wallTimeMs: number;
  /** framesProcessed / detectorTimeMs — pure detector throughput; the honest device number. */
  avgDetectorFps: number;
  hits: BenchmarkHit[];
  cappedAtMax: boolean;
}

export interface RunBenchmarkOptions {
  file: File;
  detectors: readonly ActiveFormat[];
  textPattern?: ParsedTextPattern;
  sampleRateHz?: number;
  maxSeconds?: number;
  targetLongEdgePx?: number;
  signal?: AbortSignal;
  /** Called after each processed frame with (framesDone, totalFrames). */
  onProgress?: (done: number, total: number) => void;
}

export async function runBenchmark(opts: RunBenchmarkOptions): Promise<BenchmarkResult[]> {
  // Pre-warm heavy detectors so their cold-start isn't billed to the
  // first frame's decode time (which would skew the whole run).
  if (opts.detectors.includes('aruco_4x4_1000')) {
    await ensureOpenCv().catch(() => undefined);
  }
  if (opts.detectors.includes('text')) {
    await ensureTesseract().catch(() => undefined);
  }

  const seenPerDetector = new Map<ActiveFormat, Set<string>>();
  const results: BenchmarkResult[] = opts.detectors.map((d) => ({
    detector: d,
    framesProcessed: 0,
    detectorTimeMs: 0,
    wallTimeMs: 0,
    avgDetectorFps: 0,
    hits: [],
    cappedAtMax: false,
  }));

  const wallStart = performance.now();
  const iter = iterateVideoFrames({
    file: opts.file,
    sampleRateHz: opts.sampleRateHz,
    maxSeconds: opts.maxSeconds,
    targetLongEdgePx: opts.targetLongEdgePx,
    signal: opts.signal,
  });

  let framesDone = 0;
  let lastProgressAt = 0;
  interface IteratorMeta { cappedAtMax: boolean; totalFrames: number }
  let iteratorMeta: IteratorMeta | null = null;

  while (true) {
    const step = await iter.next();
    if (step.done) {
      if (step.value) iteratorMeta = step.value as IteratorMeta;
      break;
    }
    const frame = step.value;
    for (let d = 0; d < opts.detectors.length; d++) {
      const detector = opts.detectors[d];
      const decodeStart = performance.now();
      let outcome: DecodeOutcome;
      try {
        outcome = await routeDecode(detector, frame.imageData, opts.textPattern);
      } catch {
        outcome = { kind: 'none' };
      }
      results[d].detectorTimeMs += performance.now() - decodeStart;
      results[d].framesProcessed += 1;
      if (outcome.kind === 'symbols') {
        let seen = seenPerDetector.get(detector);
        if (!seen) {
          seen = new Set();
          seenPerDetector.set(detector, seen);
        }
        for (const sym of outcome.symbols) {
          const key = sym.text.trim();
          if (key.length === 0) continue;
          if (seen.has(key)) continue;
          seen.add(key);
          results[d].hits.push({
            value: key,
            firstSeenFrame: frame.index,
            firstSeenSeconds: +frame.timeSeconds.toFixed(3),
          });
        }
      }
    }
    framesDone += 1;
    // Throttle progress-driven setState to ~10 Hz. A 60 s / 30 fps run
    // would otherwise fire onProgress 1800× per detector and reconcile
    // React on every step.
    const now = performance.now();
    if (opts.onProgress && now - lastProgressAt > 100) {
      opts.onProgress(framesDone, frame.totalFrames);
      lastProgressAt = now;
    }
    // Yield to the event loop so the tab stays responsive (Cancel button
    // reachable, no "Page unresponsive" dialog on long runs).
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
    if (opts.signal?.aborted) throw new DOMException('cancelled', 'AbortError');
  }
  // Final progress push so the caller sees the terminal state.
  opts.onProgress?.(framesDone, iteratorMeta?.totalFrames ?? framesDone);

  const wallTimeMs = performance.now() - wallStart;
  for (const r of results) {
    r.wallTimeMs = wallTimeMs;
    r.avgDetectorFps = r.detectorTimeMs > 0 ? +(r.framesProcessed / (r.detectorTimeMs / 1000)).toFixed(2) : 0;
    r.cappedAtMax = iteratorMeta?.cappedAtMax ?? false;
  }
  return results;
}

async function routeDecode(
  detector: ActiveFormat,
  imageData: ImageData,
  parsedTextPattern?: ParsedTextPattern
): Promise<DecodeOutcome> {
  switch (detector) {
    case 'qr':
      return decodeFrame(imageData);
    case 'aruco_4x4_1000':
      return decodeFiducials(imageData, 'aruco_4x4_1000');
    case 'text':
      return decodeText(imageData, parsedTextPattern ? { parsed: parsedTextPattern } : undefined);
  }
}
